<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  getVoiceCallProvider,
  isVoiceCallEnabled,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert, useProgressAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const callsStore = useCallsStore();
const whatsappCallSession = useWhatsappCallSession();
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentAccountId = useMapGetter('getCurrentAccountId');
const voiceCallProvider = computed(() => getVoiceCallProvider(props.inbox));
const isVoiceCallInbox = computed(() => {
  return (
    isVoiceCallEnabled(props.inbox) &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CHANNEL_VOICE
    )
  );
});
const isWhatsappVoiceInbox = computed(
  () => voiceCallProvider.value === VOICE_CALL_PROVIDERS.WHATSAPP
);

const isCallButtonDisabled = computed(() => {
  if (callsStore.hasActiveCall || callsStore.hasIncomingCall) return true;
  if (isWhatsappVoiceInbox.value) {
    return whatsappCallSession.isInitiating.value;
  }
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const isCallButtonLoading = computed(() =>
  isWhatsappVoiceInbox.value
    ? whatsappCallSession.isInitiating.value
    : !!contactsUiFlags.value?.isInitiatingCall
);

const callButtonTooltip = computed(() =>
  isWhatsappVoiceInbox.value
    ? t('CONVERSATION.HEADER.WHATSAPP_CALL')
    : t('CONVERSATION.HEADER.VOICE_CALL')
);

const onPermissionRequestStatus = data => {
  if (String(data?.conversation_id) !== String(props.chat.id)) return;
  if (data.status === 'delivered') {
    useAlert(t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_DELIVERED'));
  } else if (data.status === 'failed') {
    useAlert(
      t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_DELIVERY_FAILED', {
        reason: data.error?.details || '',
      })
    );
  }
};

const onPermissionGranted = data => {
  if (String(data?.conversation_id) !== String(props.chat.id)) return;
  useAlert(t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_GRANTED'));
};

onMounted(() => {
  emitter.on(
    'whatsapp_call.permission_request_status',
    onPermissionRequestStatus
  );
  emitter.on('voice_call.permission_granted', onPermissionGranted);
});

onUnmounted(() => {
  emitter.off(
    'whatsapp_call.permission_request_status',
    onPermissionRequestStatus
  );
  emitter.off('voice_call.permission_granted', onPermissionGranted);
});

const startWhatsappCall = async () => {
  if (whatsappCallSession.isInitiating.value) return;
  const dismissProgress = useProgressAlert(
    t('CONVERSATION.HEADER.WHATSAPP_CALL_INITIATING')
  );
  let response;
  try {
    response = await whatsappCallSession.initiateOutboundCall({
      conversationId: props.chat.id,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED'));
    return;
  } finally {
    dismissProgress();
  }

  try {
    // Composable returns LOCKED when init is already in flight or a call is
    // active; soft no-op so a parallel click doesn't trigger a banner.
    if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
    // Permission template path returns no call id — show banner, no widget yet.
    if (!response?.id) {
      const statusMessages = {
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_REQUESTED]:
          'WHATSAPP_CALL_PERMISSION_REQUESTED',
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING]:
          'WHATSAPP_CALL_PERMISSION_PENDING',
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_RATE_LIMITED]:
          'WHATSAPP_CALL_PERMISSION_RATE_LIMITED',
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_TEMPLATE_MISSING]:
          'WHATSAPP_CALL_PERMISSION_TEMPLATE_MISSING',
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_TEMPLATE_PENDING]:
          'WHATSAPP_CALL_PERMISSION_TEMPLATE_PENDING',
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_TEMPLATE_REJECTED]:
          'WHATSAPP_CALL_PERMISSION_TEMPLATE_REJECTED',
        [VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_TEMPLATE_UNAVAILABLE]:
          'WHATSAPP_CALL_PERMISSION_TEMPLATE_UNAVAILABLE',
      };
      const messageKey = statusMessages[response?.status];
      if (messageKey) useAlert(t(`CONVERSATION.HEADER.${messageKey}`));
      return;
    }

    // Stay non-active until Meta delivers the connect webhook (sdp_answer);
    // flipping to active here would start the duration timer before pickup.
    callsStore.addCall({
      callSid: response.call_id,
      callId: response.id,
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED'));
  }
};

const startTwilioCall = async () => {
  if (contactsUiFlags.value?.isInitiatingCall) return;
  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.chat?.meta?.sender?.id,
      inboxId: props.inbox?.id,
      conversationId: props.chat.id,
    });

    callsStore.addCall({
      callSid: response?.call_sid,
      conversationId: response?.conversation_id ?? props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.VOICE_CALL_FAILED'));
  }
};

const startCall = () => {
  if (isWhatsappVoiceInbox.value) return startWhatsappCall();
  return startTwilioCall();
};
</script>

<template>
  <NextButton
    v-if="isVoiceCallInbox"
    v-tooltip.bottom="callButtonTooltip"
    sm
    ghost
    slate
    icon="i-lucide-phone"
    :is-loading="isCallButtonLoading"
    :disabled="isCallButtonDisabled"
    @click="startCall"
  />
  <template v-else />
</template>
